import { BoardMeetingCommentWithCreatedBy } from "@boark/shared/types/board-meeting-comment-with-created-by";
import { useState } from "react";

import {
  useCreateBoardMeetingComment,
  useDeleteBoardMeetingComment,
  useUpdateBoardMeetingComment,
} from "../queries/board-meeting";
import { formatUserName } from "../services/board-meeting";
import { useLocale } from "../stores/locale";
import { formatDateTime } from "../utils/date";
import { Button } from "./button";
import { Confirm } from "./confirm";

const textareaClassName =
  "w-full border-1 border-gray-300 rounded-xs p-2 min-h-[3.5rem] bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

export function BoardMeetingContentComments({
  meetingId,
  contentId,
  comments,
  authUserId,
  canComment,
}: {
  meetingId: string;
  contentId: string;
  comments: BoardMeetingCommentWithCreatedBy[];
  authUserId?: string;
  canComment: boolean;
}) {
  const { locale: t } = useLocale();
  const createComment = useCreateBoardMeetingComment(meetingId);
  const [newComment, setNewComment] = useState("");

  const handleAddComment = async () => {
    const trimmed = newComment.trim();
    if (trimmed === "") {
      return;
    }
    await createComment.mutateAsync({
      contentId,
      comment: { comment: trimmed },
    });
    setNewComment("");
  };

  return (
    <div className="flex flex-col gap-3">
      {comments.length > 0 ? (
        <ul className="flex flex-col gap-2 list-none m-0 p-0">
          {comments.map((comment) => (
            <li key={comment.id}>
              <CommentRow
                canModify={canComment && authUserId === comment.createdBy.id}
                comment={comment}
                meetingId={meetingId}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 m-0">
          {t.boardMeeting.commentsEmpty}
        </p>
      )}
      {canComment && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleAddComment();
          }}
        >
          <textarea
            className={textareaClassName}
            disabled={createComment.isPending}
            placeholder={t.boardMeeting.commentPlaceholder}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <div className="self-end">
            <Button
              disabled={newComment.trim() === ""}
              loading={createComment.isPending}
              size="sm"
              type="submit"
            >
              {t.boardMeeting.commentAdd}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function CommentRow({
  comment,
  meetingId,
  canModify,
}: {
  comment: BoardMeetingCommentWithCreatedBy;
  meetingId: string;
  canModify: boolean;
}) {
  const { locale: t, get: getLocale } = useLocale();
  const updateComment = useUpdateBoardMeetingComment(meetingId);
  const deleteComment = useDeleteBoardMeetingComment(meetingId);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(comment.comment);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // createdAt and updatedAt are set separately on create, so allow for a
  // small difference before showing the comment as edited
  const wasEdited =
    new Date(comment.updatedAt).getTime() -
      new Date(comment.createdAt).getTime() >
    1000;

  const confirmEdit = async () => {
    const trimmed = editValue.trim();
    if (trimmed === "") {
      return;
    }
    await updateComment.mutateAsync({
      commentId: comment.id,
      comment: { comment: trimmed },
    });
    setEditing(false);
  };

  return (
    <div className="flex flex-col gap-1 border border-gray-200 rounded-xs p-2 bg-white">
      <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-600">
        <span className="font-medium text-gray-900">
          {formatUserName(comment.createdBy)}
        </span>
        <span>{formatDateTime(comment.createdAt, false)}</span>
        {wasEdited && (
          <span className="italic">({t.boardMeeting.commentEdited})</span>
        )}
      </div>
      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            className={textareaClassName}
            disabled={updateComment.isPending}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              disabled={editValue.trim() === ""}
              loading={updateComment.isPending}
              size="sm"
              onClick={() => void confirmEdit()}
            >
              {t.page.save}
            </Button>
            <Button
              disabled={updateComment.isPending}
              severity="secondary"
              size="sm"
              onClick={() => {
                setEditValue(comment.comment);
                setEditing(false);
              }}
            >
              {t.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <p className="m-0 text-sm whitespace-pre-wrap">{comment.comment}</p>
      )}
      {canModify && !editing && (
        <div className="flex gap-2">
          <Button
            severity="secondary"
            size="sm"
            onClick={() => {
              setEditValue(comment.comment);
              setEditing(true);
            }}
          >
            {t.page.edit}
          </Button>
          <Button
            severity="danger"
            size="sm"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            {t.boardMeeting.commentDelete}
          </Button>
        </div>
      )}
      {deleteConfirmOpen && (
        <Confirm
          header={getLocale("boardMeeting.commentConfirmDelete")}
          loading={deleteComment.isPending}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            void (async () => {
              await deleteComment.mutateAsync(comment.id);
              setDeleteConfirmOpen(false);
            })();
          }}
        />
      )}
    </div>
  );
}
